/**
 * A client tag such as "RL" or "RC". Not a closed list, so a new client only
 * needs to add `?source=` to its link.
 * @typedef {string} ContactTicketSource
 */

/**
 * @typedef {{
 *   requestId: string,
 *   name: string,
 *   email: string,
 *   language: string,
 *   description: string,
 *   source: ContactTicketSource,
 *   country?: string,
 *   type?: string,
 * }} ContactTicket
 */

/**
 * The fields of the Salesforce case that the ticket mirrors.
 * @typedef {{
 *   name: string,
 *   email: string,
 *   language: string,
 *   description: string,
 *   type?: string,
 *   country?: string,
 * }} ContactCase
 */

export const CONTACT_TICKET_SOURCE_PARAM = "source"
export const CONTACT_TICKET_SOURCE_STORAGE_KEY = "contact-ticket-source"
/** @type {ContactTicketSource} */
export const DEFAULT_CONTACT_TICKET_SOURCE = "GID"

/**
 * Uppercased so a client that sends "rl" is filed with "RL".
 * @param {string | null} value
 * @returns {ContactTicketSource | undefined}
 */
const parseSource = value => value?.trim().toUpperCase() || undefined

/**
 * Stores a non-empty `?source=` for this tab. An absent or empty param keeps the
 * stored value, because users come back from Cognito or social login without it.
 * @param {string} search
 */
export const captureContactTicketSource = search => {
  const source = parseSource(
    new URLSearchParams(search).get(CONTACT_TICKET_SOURCE_PARAM),
  )
  if (!source) return

  try {
    sessionStorage.setItem(CONTACT_TICKET_SOURCE_STORAGE_KEY, source)
  } catch {
    // Storage can be blocked; the ticket then falls back to the default source.
  }
}

/** @returns {ContactTicketSource} */
export const getContactTicketSource = () => {
  try {
    const stored = parseSource(
      sessionStorage.getItem(CONTACT_TICKET_SOURCE_STORAGE_KEY),
    )
    if (stored) return stored
  } catch {
    // Same as nothing stored.
  }
  return DEFAULT_CONTACT_TICKET_SOURCE
}

/**
 * @param {{
 *   contactCase: ContactCase,
 *   requestId: string,
 *   source: ContactTicketSource,
 * }} input
 * @returns {ContactTicket}
 */
export const buildContactTicket = ({contactCase, requestId, source}) => ({
  requestId,
  name: contactCase.name,
  email: contactCase.email,
  language: contactCase.language,
  description: contactCase.description,
  source,
  ...(contactCase.country ? {country: contactCase.country} : {}),
  ...(contactCase.type ? {type: contactCase.type} : {}),
})
